// @ts-nocheck
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { AlertTriangle, PartyPopper } from 'lucide-react';
import { invokeEdgeFunction } from "@/lib/invokeEdgeFunction";

const baseSchema = z.object({
  claimer_email: z.string().trim().email('Valid email required').max(255),
  claimer_phone: z.string().trim().max(20).optional().or(z.literal('')),
  allow_college_coach_contact: z.boolean().default(true),
});

type FormValues = z.infer<typeof baseSchema>;

export default function PlayerClaimPage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [success, setSuccess] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [password, setPassword] = useState('');
  const [signupLoading, setSignupLoading] = useState(false);

  const {
    data: preview,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['player-claim-preview', token],
    queryFn: async () => {
      const { data, error: invokeError } = await invokeEdgeFunction(
        'rle-accept-player-claim',
        { body: { token, preview: true } }
      );
      if (invokeError) throw invokeError;
      if (!data?.ok) throw new Error(data?.error?.code || 'TOKEN_INVALID');
      return data.data as {
        valid: boolean;
        player: {
          canonical_first_name: string;
          canonical_last_name: string;
          grad_year: number | null;
          email?: string | null;
          current_team_id: string | null;
        };
        target: 'athlete' | 'parent';
        expires_at: string;
      };
    },
    enabled: !!token,
    retry: false,
    staleTime: Infinity,
  });

  const form = useForm<FormValues>({
    resolver: zodResolver(baseSchema),
    defaultValues: {
      claimer_email: '',
      claimer_phone: '',
      allow_college_coach_contact: true,
    },
  });

  // Pre-fill email from preview once it loads
  useEffect(() => {
    const previewEmail = preview?.player?.email;
    if (previewEmail && !form.getValues('claimer_email')) {
      form.setValue('claimer_email', previewEmail);
    }
  }, [preview, form]);

  const submitMutation = useMutation({
    mutationFn: async (values: FormValues) => {
      const { data, error: invokeError } = await invokeEdgeFunction(
        'rle-accept-player-claim',
        {
          body: {
            token,
            claimer_email: values.claimer_email,
            claimer_phone: values.claimer_phone || null,
            profile_fields: {
              allow_college_coach_contact: values.allow_college_coach_contact,
            },
          },
        }
      );
      if (invokeError) throw invokeError;
      if (!data?.ok) {
        const code = data?.error?.code;
        const message = data?.error?.message || 'Failed to claim profile';
        const err = new Error(message) as Error & { code?: string };
        err.code = code;
        throw err;
      }
      return data.data;
    },
    onSuccess: () => {
      setSuccess(true);
      setShowConfetti(true);
      setTimeout(() => setShowConfetti(false), 2000);
    },
    onError: (err: Error & { code?: string }) => {
      switch (err.code) {
        case 'COPPA_PARENT_REQUIRED':
          toast.error('This profile needs a parent to claim');
          break;
        case 'TOKEN_EXPIRED':
          toast.error('Claim link expired — ask your team to resend');
          break;
        case 'TOKEN_ALREADY_USED':
          toast.error('This profile has already been claimed');
          break;
        case 'TOKEN_INVALID':
          toast.error("This link isn't valid");
          break;
        default:
          toast.error(err.message || 'Something went wrong');
      }
    },
  });

  const onSubmit = (values: FormValues) => {
    submitMutation.mutate(values);
  };

  const handleCreateAccount = async () => {
    if (!password || password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    setSignupLoading(true);
    try {
      const { error: signupError } = await supabase.auth.signUp({
        email: form.getValues('claimer_email'),
        password,
        options: { emailRedirectTo: `${window.location.origin}/` },
      });
      if (signupError) throw signupError;
      toast.success('Account created! Check your email to verify.');
      navigate('/');
    } catch (e: any) {
      toast.error(e.message || 'Signup failed');
    } finally {
      setSignupLoading(false);
    }
  };

  // Loading
  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-[560px]">
          <CardHeader>
            <Skeleton className="h-6 w-40" />
            <Skeleton className="h-4 w-64" />
          </CardHeader>
          <CardContent className="space-y-3">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </CardContent>
        </Card>
      </div>
    );
  }

  // Error states
  if (error) {
    const code = (error as Error).message;
    let title = 'Invalid link';
    let description = "This link isn't valid.";
    if (code === 'TOKEN_EXPIRED') {
      title = 'Link expired';
      description = 'This link has expired. Ask your coach to resend it.';
    } else if (code === 'TOKEN_ALREADY_USED') {
      title = 'Already claimed';
      description = 'This profile has already been claimed.';
    } else if (code === 'COPPA_PARENT_REQUIRED') {
      title = 'Parent claim required';
      description = 'This profile needs a parent to claim.';
    }
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-[560px]">
          <CardHeader className="text-center">
            <p className="text-xs font-bold tracking-wider text-muted-foreground mb-4">RLEVENTS</p>
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>{title}</AlertTitle>
              <AlertDescription>{description}</AlertDescription>
            </Alert>
          </CardHeader>
          <CardContent className="text-center">
            {code === 'TOKEN_ALREADY_USED' && (
              <Button onClick={() => navigate('/login')}>Sign in</Button>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!preview) return null;

  const firstName = preview.player.canonical_first_name;
  const lastName = preview.player.canonical_last_name;
  const gradYear = preview.player.grad_year;

  // Success state
  if (success) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4 relative overflow-hidden">
        {showConfetti && <ConfettiBurst />}
        <Card className="w-full max-w-[560px] relative z-10">
          <CardHeader className="text-center">
            <p className="text-xs font-bold tracking-wider text-muted-foreground mb-4">RLEVENTS</p>
            <PartyPopper className="h-12 w-12 mx-auto text-primary mb-2" />
            <CardTitle className="text-2xl">Welcome to RLevents, {firstName}!</CardTitle>
            <CardDescription>Your profile is claimed and visible to coaches.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {showPasswordForm ? (
              <div className="space-y-3">
                <FormLabel>Set a password to sign in later</FormLabel>
                <Input
                  type="password"
                  placeholder="At least 8 characters"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <div className="flex gap-2">
                  <Button onClick={handleCreateAccount} disabled={signupLoading} className="flex-1">
                    {signupLoading ? 'Creating...' : 'Create account'}
                  </Button>
                  <Button variant="outline" onClick={() => setShowPasswordForm(false)}>
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex flex-col sm:flex-row gap-2">
                <Button onClick={() => setShowPasswordForm(true)} className="flex-1">
                  Create an account
                </Button>
                <Button variant="outline" onClick={() => navigate('/')} className="flex-1">
                  Skip for now
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // Confirmation form
  const heading = `Claim ${firstName} ${lastName}'s profile`;

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4 py-12">
      <Card className="w-full max-w-[560px]">
        <CardHeader className="text-center">
          <p className="text-xs font-bold tracking-wider text-muted-foreground mb-4">RLEVENTS</p>
          <CardTitle className="text-2xl">{heading}</CardTitle>
          {gradYear && (
            <p className="text-sm font-semibold text-[#06B6D4]">Class of {gradYear}</p>
          )}
          <CardDescription>
            Confirm your details below to claim this profile.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="claimer_email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input type="email" {...field} />
                    </FormControl>
                    <FormDescription>Confirm the email for this profile.</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="claimer_phone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Phone (optional)</FormLabel>
                    <FormControl>
                      <Input formatPhone {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="allow_college_coach_contact"
                render={({ field }) => (
                  <FormItem className="flex items-start gap-3 rounded-md border p-3">
                    <FormControl>
                      <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel>
                        Allow college coaches to discover and contact this profile
                      </FormLabel>
                      <FormDescription>
                        You can change this anytime after claiming.
                      </FormDescription>
                    </div>
                  </FormItem>
                )}
              />

              <Button
                type="submit"
                size="lg"
                className="w-full"
                disabled={submitMutation.isPending}
              >
                {submitMutation.isPending ? 'Claiming...' : 'Claim profile'}
              </Button>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
}

function ConfettiBurst() {
  const pieces = Array.from({ length: 60 });
  return (
    <div className="pointer-events-none fixed inset-0 z-0">
      {pieces.map((_, i) => {
        const left = Math.random() * 100;
        const delay = Math.random() * 0.5;
        const duration = 1.2 + Math.random() * 0.8;
        const colors = ['hsl(var(--primary))', 'hsl(var(--accent))', 'hsl(var(--secondary))'];
        const color = colors[i % colors.length];
        return (
          <span
            key={i}
            style={{
              position: 'absolute',
              top: '-10px',
              left: `${left}%`,
              width: 8,
              height: 14,
              backgroundColor: color,
              transform: `rotate(${Math.random() * 360}deg)`,
              animation: `confetti-fall ${duration}s ${delay}s linear forwards`,
            }}
          />
        );
      })}
      <style>{`
        @keyframes confetti-fall {
          to { transform: translateY(110vh) rotate(720deg); opacity: 0; }
        }
      `}</style>
    </div>
  );
}
